import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { Loader2 } from 'lucide-react';

export default function DespesaModal({ open, onOpenChange, despesa, onSuccess }) {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        descricao: '',
        valor: '',
        data_vencimento: '',
        status: 'pendente',
        categoria: 'geral'
    });

    useEffect(() => {
        if (despesa) {
            setFormData({
                descricao: despesa.descricao || '',
                valor: despesa.valor || '',
                data_vencimento: despesa.data_vencimento || '',
                status: despesa.status || 'pendente',
                categoria: despesa.categoria || 'geral'
            });
        } else {
            setFormData({
                descricao: '',
                valor: '',
                data_vencimento: new Date().toISOString().split('T')[0],
                status: 'pendente',
                categoria: 'geral'
            });
        }
    }, [despesa, open]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (despesa?.id) {
                await api.despesas.update(despesa.id, formData);
                toast({ title: "Sucesso", description: "Despesa atualizada com sucesso." });
            } else {
                await api.despesas.create(formData);
                toast({ title: "Sucesso", description: "Despesa cadastrada com sucesso." });
            }
            onSuccess?.();
            onOpenChange(false);
        } catch (error) {
            toast({
                title: "Erro ao salvar",
                description: error.error || "Ocorreu um erro ao processar sua solicitação.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-[#193D6D]">{despesa ? 'Editar Despesa' : 'Nova Despesa'}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="descricao">Descrição / Fornecedor</Label>
                        <Input
                            id="descricao"
                            value={formData.descricao}
                            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                            placeholder="Ex: Aluguel, Software, Papelaria..."
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="valor">Valor (R$)</Label>
                            <Input
                                id="valor"
                                type="number"
                                step="0.01"
                                value={formData.valor}
                                onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                                placeholder="0,00"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="data_vencimento">Vencimento</Label>
                            <Input
                                id="data_vencimento"
                                type="date"
                                value={formData.data_vencimento}
                                onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="status">Status</Label>
                            <Select
                                value={formData.status}
                                onValueChange={(val) => setFormData({ ...formData, status: val })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="pendente">Pendente</SelectItem>
                                    <SelectItem value="pago">Pago</SelectItem>
                                    <SelectItem value="atrasado">Atrasado</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="categoria">Categoria</Label>
                            <Select
                                value={formData.categoria}
                                onValueChange={(val) => setFormData({ ...formData, categoria: val })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Selecione" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="geral">Geral</SelectItem>
                                    <SelectItem value="infraestrutura">Infraestrutura</SelectItem>
                                    <SelectItem value="pessoal">Pessoal</SelectItem>
                                    <SelectItem value="marketing">Marketing</SelectItem>
                                    <SelectItem value="tecnologia">Tecnologia</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter className="pt-4">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading} className="bg-[#193D6D] hover:bg-blue-800">
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {despesa ? 'Salvar Alterações' : 'Confirmar Despesa'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
